// Copyright (C) 2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import Menu from 'antd/lib/menu';
import Modal from 'antd/lib/modal';
// eslint-disable-next-line import/no-extraneous-dependencies
import { MenuInfo } from 'rc-menu/lib/interface';
import { exportActions } from 'actions/export-actions';

import {
    Job, JobStage, getCore, User,
} from 'cvat-core-wrapper';
import { deleteJobAsync } from 'actions/jobs-actions';
import { importActions } from 'actions/import-actions';
import { GetJobStageChinese, JobState } from 'cvat-core/src/enums';
import { Actions } from 'components/annotation-page/top-bar/annotation-menu';
import { modelsActions } from 'actions/models-actions';
import { hasPermissionForJobAfterQualityCtlStage } from 'utils/permissions';

const core = getCore();

interface Props {
    job: Job;
    user: User;
    onJobUpdate: (job: Job) => void;
}

function JobActionsMenu(props: Props): JSX.Element {
    const { job, user, onJobUpdate } = props;
    const history = useHistory();
    const dispatch = useDispatch();

    const onDelete = useCallback(() => {
        Modal.confirm({
            title: `将删除作业 #${job.id}`,
            content: '所有相关数据（标签）都会丢失。是否继续？',
            className: 'cvat-modal-confirm-delete-job',
            onOk: () => {
                dispatch(deleteJobAsync(job));
            },
            okButtonProps: {
                type: 'primary',
                danger: true,
            },
            okText: '删除',
            cancelText: '取消',
        });
    }, [job]);

    return (
        <Menu onClick={(action: MenuInfo) => {
            if (action.key === 'task') {
                history.push(`/tasks/${job.taskId}`);
            } else if (action.key === 'project') {
                history.push(`/projects/${job.projectId}`);
            } else if (action.key === 'bug_tracker') {
                if (job.bugTracker) window.open(job.bugTracker, '_blank', 'noopener noreferrer');
            } else if (action.key === 'import_job') {
                dispatch(importActions.openImportDatasetModal(job));
            } else if (action.key === 'export_job') {
                dispatch(exportActions.openExportDatasetModal(job));
            } else if (action.key === 'view_analytics') {
                history.push(`/tasks/${job.taskId}/jobs/${job.id}/analytics`);
            } else if (action.key === Actions.AUTO_ANNOTATE_JOB) {
                dispatch(modelsActions.showRunModelDialogForJob(job));
            } else if (action.key === Actions.FINISH_ANNOTATION) {
                Modal.confirm({
                    title: `您是否想将当前作业的阶段切换到 ${GetJobStageChinese(JobStage.VALIDATION)}`,
                    content: `阶段将被切换到：${GetJobStageChinese(JobStage.VALIDATION)}。是否继续？`,
                    okText: '继续',
                    cancelText: '取消',
                    className: 'cvat-modal-content-finish-job',
                    onOk: () => {
                        job.stage = JobStage.VALIDATION;
                        job.state = core.enums.JobState.NEW;
                        onJobUpdate(job);
                    },
                });
            } else if (action.key === Actions.FINISH_VALIDATION) {
                Modal.confirm({
                    title: `您是否想将当前作业的阶段切换到 ${GetJobStageChinese(JobStage.INSPECTION)}`,
                    content: `阶段将被切换到：${GetJobStageChinese(JobStage.INSPECTION)}。是否继续？`,
                    okText: '继续',
                    cancelText: '取消',
                    className: 'cvat-modal-content-finish-job',
                    onOk: () => {
                        job.state = core.enums.JobState.NEW;
                        job.stage = JobStage.INSPECTION;
                        onJobUpdate(job);
                    },
                });
            } else if (action.key === Actions.REVERT_VALIDATION) {
                Modal.confirm({
                    title: `您是否想将当前作业的阶段切换到 ${GetJobStageChinese(JobStage.ANNOTATION)}`,
                    content: `阶段将被切换到：${GetJobStageChinese(JobStage.ANNOTATION)}。是否继续？`,
                    okText: '继续',
                    cancelText: '取消',
                    className: 'cvat-modal-content-finish-job',
                    onOk: () => {
                        job.state = core.enums.JobState.REJECTED;
                        job.stage = JobStage.ANNOTATION;
                        onJobUpdate(job);
                    },
                });
            } else if (action.key === Actions.FINISH_INSPECTION) {
                Modal.confirm({
                    title: `您是否想将当前作业的阶段切换到 ${GetJobStageChinese(JobStage.QUALITYCTL)}`,
                    content: `阶段将被切换到：${GetJobStageChinese(JobStage.QUALITYCTL)}。是否继续？`,
                    okText: '继续',
                    cancelText: '取消',
                    className: 'cvat-modal-content-finish-job',
                    onOk: () => {
                        job.state = core.enums.JobState.NEW;
                        job.stage = JobStage.QUALITYCTL;
                        onJobUpdate(job);
                    },
                });
            } else if (action.key === Actions.REVERT_INSPECTION) {
                Modal.confirm({
                    title: `您是否想将当前作业的阶段回退到 ${GetJobStageChinese(JobStage.VALIDATION)}`,
                    content: `阶段将被回退到：${GetJobStageChinese(JobStage.VALIDATION)}。是否继续？`,
                    okText: '继续',
                    cancelText: '取消',
                    className: 'cvat-modal-content-finish-job',
                    onOk: () => {
                        job.state = core.enums.JobState.REJECTED;
                        job.stage = JobStage.VALIDATION;
                        onJobUpdate(job);
                    },
                });
            } else if (action.key === Actions.FINISH_QUALITYCTL) {
                Modal.confirm({
                    title: `您是否想将当前作业的阶段切换到 ${GetJobStageChinese(JobStage.ACCEPTANCE)}`,
                    content: `阶段将被切换到：${GetJobStageChinese(JobStage.ACCEPTANCE)}。是否继续？`,
                    okText: '继续',
                    cancelText: '取消',
                    className: 'cvat-modal-content-finish-job',
                    onOk: () => {
                        job.state = core.enums.JobState.COMPLETED;
                        job.stage = JobStage.ACCEPTANCE;
                        onJobUpdate(job);
                    },
                });
            } else if (action.key === Actions.REVERT_QUALITYCTL) {
                Modal.confirm({
                    title: `您是否想将当前作业的阶段切换到 ${GetJobStageChinese(JobStage.INSPECTION)}`,
                    content: `阶段将被切换到：${GetJobStageChinese(JobStage.INSPECTION)}。是否继续？`,
                    okText: '继续',
                    cancelText: '取消',
                    className: 'cvat-modal-content-finish-job',
                    onOk: () => {
                        job.state = core.enums.JobState.REJECTED;
                        job.stage = JobStage.INSPECTION;
                        onJobUpdate(job);
                    },
                });
            } else if (action.key === Actions.FINISH_ACCEPTANCE) {
                Modal.confirm({
                    title: `您是否想将当前作业的阶段切换到 ${GetJobStageChinese(JobStage.ACCEPTANCE)}`,
                    content: `阶段将被切换到：${GetJobStageChinese(JobStage.ACCEPTANCE)}。是否继续？`,
                    okText: '继续',
                    cancelText: '取消',
                    className: 'cvat-modal-content-finish-job',
                    onOk: () => {
                        job.state = core.enums.JobState.COMPLETED;
                        job.stage = JobStage.ACCEPTANCE;
                        onJobUpdate(job);
                    },
                });
            } else if (action.key === Actions.RENEW_JOB) {
                Modal.confirm({
                    title: '您是否想重置当前作业？',
                    content: `阶段将被切换到：${GetJobStageChinese(JobStage.ANNOTATION)}。是否继续？`,
                    okText: '继续',
                    cancelText: '取消',
                    className: 'cvat-modal-content-finish-job',
                    onOk: () => {
                        job.state = core.enums.JobState.NEW;
                        job.stage = JobStage.ANNOTATION;
                        onJobUpdate(job);
                    },
                });
            }
        }}
        >
            {user?.isSuperuser ? (
                <>
                    <Menu.Item key='task' disabled={job.taskId === null}>跳转到任务页面</Menu.Item>
                    <Menu.Item key='project' disabled={job.projectId === null}>跳转到项目页面</Menu.Item>
                    {user?.isStaff ? (
                        <>
                            {/* <Menu.Item key='bug_tracker' disabled={!job.bugTracker}>跳转到问题追踪页面</Menu.Item> */}
                            <Menu.Item key='import_job' disabled={job.locked}>导入标注</Menu.Item>
                            <Menu.Item key='export_job'>导出标注</Menu.Item>
                            <Menu.Item key={Actions.AUTO_ANNOTATE_JOB} disabled={job.locked}>自动标注</Menu.Item>
                            <Menu.Item key='delete' onClick={() => onDelete()} disabled={job.locked}>删除作业</Menu.Item>
                        </>
                    ) : null}
                    <Menu.Divider />
                </>
            ) : null}

            <Menu.Item key='view_analytics'>查看分析</Menu.Item>

            <Menu.Divider />

            {[JobStage.ANNOTATION].includes(job.stage) &&
                [JobState.NEW, JobState.IN_PROGRESS, JobState.REJECTED].includes(job.state) ?
                <Menu.Item key={Actions.FINISH_ANNOTATION} disabled={job.locked}>完成标注</Menu.Item> : null}

            {[JobStage.VALIDATION].includes(job.stage) &&
                [JobState.NEW, JobState.IN_PROGRESS, JobState.REJECTED].includes(job.state) ?
                <Menu.Item key={Actions.FINISH_VALIDATION} disabled={job.locked}>完成审核</Menu.Item> : null}
            {[JobStage.VALIDATION].includes(job.stage) &&
                [JobState.NEW, JobState.IN_PROGRESS, JobState.REJECTED].includes(job.state) ?
                <Menu.Item key={Actions.REVERT_VALIDATION} disabled={job.locked}>退回标注</Menu.Item> : null}

            {[JobStage.INSPECTION].includes(job.stage) &&
                [JobState.NEW, JobState.IN_PROGRESS, JobState.REJECTED].includes(job.state) ?
                hasPermissionForJobAfterQualityCtlStage(user) &&
                <Menu.Item key={Actions.FINISH_INSPECTION} disabled={job.locked}>完成质检</Menu.Item> : null}

            {[JobStage.QUALITYCTL].includes(job.stage) &&
                [JobState.NEW, JobState.IN_PROGRESS, JobState.REJECTED].includes(job.state) ?
                hasPermissionForJobAfterQualityCtlStage(user) &&
                <Menu.Item key={Actions.FINISH_QUALITYCTL}>完成质控</Menu.Item> : null}
            {[JobStage.QUALITYCTL].includes(job.stage) &&
                [JobState.NEW, JobState.IN_PROGRESS, JobState.REJECTED].includes(job.state) ?
                hasPermissionForJobAfterQualityCtlStage(user) &&
                <Menu.Item key={Actions.REVERT_INSPECTION}>退回审核</Menu.Item> : null}
            {[JobStage.QUALITYCTL].includes(job.stage) &&
                [JobState.NEW, JobState.IN_PROGRESS, JobState.REJECTED].includes(job.state) ?
                hasPermissionForJobAfterQualityCtlStage(user) &&
                <Menu.Item key={Actions.REVERT_QUALITYCTL} disabled={job.locked}>退回质检</Menu.Item> : null}

            {[JobStage.ACCEPTANCE].includes(job.stage) &&
                [JobState.NEW, JobState.IN_PROGRESS, JobState.REJECTED].includes(job.state) ?
                hasPermissionForJobAfterQualityCtlStage(user) &&
                <Menu.Item key={Actions.FINISH_ACCEPTANCE} disabled={job.locked}>完成验收</Menu.Item> : null}

            {[JobStage.ACCEPTANCE].includes(job.stage) && [JobState.COMPLETED].includes(job.state) ?
                hasPermissionForJobAfterQualityCtlStage(user) &&
                <Menu.Item key={Actions.RENEW_JOB} disabled={job.locked}>再次标注</Menu.Item> : null}

        </Menu>
    );
}

export default React.memo(JobActionsMenu);
